import { ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { Base, WarningCircleIcon } from '@itsrever/design-system'
import {
  ALWAYS_COVERED,
  DEFAULT_CUSTOM_THRESHOLD,
  FREE_SHIPPING_LINES,
  ExchangeFlow,
  ExchangeOrders,
  ExchangePolicy,
  InstantExchange,
  InstantExchangeFeeType,
  InstantExchangeTiming,
  OrderPrice,
  PriceCover,
  ShippingLine,
  priceCover
} from '@/domain/exchanges'
import {
  Choice,
  FormProps,
  NumberField,
  Option,
  SettingsCard,
  SwitchRow
} from '@/ui/components/group-settings'

const { Callout } = Base

export type { FormProps }

const OFFERED_FLOWS: ExchangeFlow[] = ['NO_EXCHANGE_FLOW', 'ONE_ON_ONE', 'FULL']
const COVERS: PriceCover[] = ['ALWAYS', 'NEVER', 'CUSTOM']

const flowLabel = (t: TFunction, flow: ExchangeFlow) =>
  t(`exchanges.policy.flow.${flow}.label`)

// Amounts are stored in cents and edited in the store's currency.
const toCents = (amount: number) => Math.max(0, Math.round(amount * 100))

export const PolicyForm: React.FC<
  FormProps<ExchangePolicy> & {
    // Whether the platform has a Full Catalog to offer.
    fullCatalog: boolean
    // Points to the Full Catalog tab when Same product + Full Catalog is picked.
    catalogNote?: ReactNode
  }
> = ({ value, saved, onChange, readOnly, fullCatalog, catalogNote }) => {
  const { t } = useTranslation()
  const offered = fullCatalog
    ? OFFERED_FLOWS
    : OFFERED_FLOWS.filter((flow) => flow !== 'FULL')
  // A flow not offered here (the open ones, set by REVER) still shows to a group already on it.
  const flows = offered.includes(saved.exchangeFlow)
    ? offered
    : [...offered, saved.exchangeFlow]
  const cover = priceCover(value.threshold)
  const thresholdOf = (next: PriceCover) =>
    next === 'ALWAYS'
      ? ALWAYS_COVERED
      : next === 'NEVER'
        ? 0
        : cover === 'CUSTOM'
          ? value.threshold
          : DEFAULT_CUSTOM_THRESHOLD
  return (
    <>
      <SettingsCard
        title={t('exchanges.policy.type_title')}
        description={t('exchanges.policy.type_description')}
      >
        {value.exchangeFlow !== saved.exchangeFlow ? (
          <Callout
            tone="danger"
            icon={<WarningCircleIcon />}
            className="text-xs"
          >
            {t('exchanges.policy.flow_change', {
              shopper: t(`exchanges.policy.flow.${value.exchangeFlow}.shopper`)
            })}
            {saved.exchangeFlow === 'FULL'
              ? ` ${t('exchanges.policy.flow_change_leaving_full')}`
              : null}
          </Callout>
        ) : null}
        <Choice
          label={t('exchanges.policy.type_title')}
          value={value.exchangeFlow}
          options={flows.map((flow) => ({
            value: flow,
            label: flowLabel(t, flow),
            description: t(`exchanges.policy.flow.${flow}.description`)
          }))}
          onChange={(exchangeFlow) => onChange({ ...value, exchangeFlow })}
          readOnly={readOnly}
        />
        {value.exchangeFlow === 'FULL' ? catalogNote : null}
      </SettingsCard>
      {value.exchangeFlow === 'NO_EXCHANGE_FLOW' ? null : (
        <SettingsCard
          title={t('exchanges.policy.cover_title')}
          description={t('exchanges.policy.cover_description')}
        >
          <Choice
            label={t('exchanges.policy.cover_title')}
            value={cover}
            options={COVERS.map((option) => ({
              value: option,
              label: t(`exchanges.policy.cover.${option}.label`),
              description: t(`exchanges.policy.cover.${option}.description`)
            }))}
            onChange={(next) =>
              onChange({ ...value, threshold: thresholdOf(next) })
            }
            readOnly={readOnly}
          />
          {cover === 'CUSTOM' ? (
            <NumberField
              label={t('exchanges.policy.cover_amount')}
              value={value.threshold / 100}
              onChange={(amount) =>
                onChange({ ...value, threshold: Math.max(1, toCents(amount)) })
              }
              readOnly={readOnly}
            />
          ) : null}
        </SettingsCard>
      )}
      <SettingsCard title={t('exchanges.policy.stock_title')}>
        <SwitchRow
          label={t('exchanges.policy.stock_label')}
          description={t('exchanges.policy.stock_description')}
          checked={value.destinationScopedStock}
          onChange={(destinationScopedStock) =>
            onChange({ ...value, destinationScopedStock })
          }
          readOnly={readOnly}
        />
      </SettingsCard>
    </>
  )
}

const PRICES: OrderPrice[] = ['FULL', 'ZERO']
const SHIPPING_LINES: ShippingLine[] = ['ZERO', 'ZERO_WITH_ZONE', 'CHEAPEST']
// The empty payment method, in the form's options.
const EXCHANGE_BY_REVER = 'EXCHANGE_BY_REVER'

export const OrdersForm: React.FC<
  FormProps<ExchangeOrders> & {
    // The discounts are Shopify's and the payment method PrestaShop's.
    showDiscounts: boolean
    showPaymentMethod: boolean
  }
> = ({
  value,
  saved,
  onChange,
  readOnly,
  showDiscounts,
  showPaymentMethod
}) => {
  const { t } = useTranslation()
  // ORIGINAL is Salesforce only and set by REVER: shown only to a group already on it.
  const lines =
    saved.shippingLine === 'ORIGINAL'
      ? [...SHIPPING_LINES, 'ORIGINAL' as const]
      : SHIPPING_LINES
  const option = <V extends string>(prefix: string, v: V): Option<V> => ({
    value: v,
    label: t(`${prefix}.${v}.label`),
    description: t(`${prefix}.${v}.description`)
  })
  return (
    <>
      <SettingsCard title={t('exchanges.orders.price_title')}>
        <Choice
          label={t('exchanges.orders.price_title')}
          value={value.price}
          options={PRICES.map((price) =>
            option('exchanges.orders.price', price)
          )}
          // A zero-price order always ships for free.
          onChange={(price) =>
            onChange({
              ...value,
              price,
              shippingLine:
                price === 'ZERO' &&
                !FREE_SHIPPING_LINES.includes(value.shippingLine)
                  ? 'ZERO'
                  : value.shippingLine
            })
          }
          readOnly={readOnly}
        />
      </SettingsCard>
      <SettingsCard
        title={t('exchanges.orders.shipping_title')}
        description={
          value.price === 'ZERO'
            ? t('exchanges.orders.zero_price_note')
            : undefined
        }
      >
        <Choice
          label={t('exchanges.orders.shipping_title')}
          value={value.shippingLine}
          options={lines.map((line) => ({
            ...option('exchanges.orders.shipping', line),
            disabled:
              value.price === 'ZERO' && !FREE_SHIPPING_LINES.includes(line)
          }))}
          onChange={(shippingLine) => onChange({ ...value, shippingLine })}
          readOnly={readOnly}
        />
      </SettingsCard>
      {showPaymentMethod ? (
        <SettingsCard
          title={t('exchanges.orders.payment_title')}
          description={t('exchanges.orders.payment_description')}
        >
          <Choice
            label={t('exchanges.orders.payment_title')}
            value={value.paymentMethod || EXCHANGE_BY_REVER}
            options={([EXCHANGE_BY_REVER, 'SAME_AS_ORIGINAL'] as const).map(
              (method) => option('exchanges.orders.payment', method)
            )}
            onChange={(method) =>
              onChange({
                ...value,
                paymentMethod: method === EXCHANGE_BY_REVER ? '' : method
              })
            }
            readOnly={readOnly}
          />
        </SettingsCard>
      ) : null}
      {showDiscounts ? (
        <SettingsCard title={t('exchanges.orders.discounts_title')}>
          <SwitchRow
            label={t('exchanges.orders.carry_over_label')}
            description={t('exchanges.orders.carry_over_description')}
            checked={value.carryOverDiscounts}
            onChange={(carryOverDiscounts) =>
              onChange({ ...value, carryOverDiscounts })
            }
            readOnly={readOnly}
          />
        </SettingsCard>
      ) : null}
    </>
  )
}

const TIMINGS: InstantExchangeTiming[] = [
  'ON_START_RETURN_PROCESS',
  'ON_ITEM_SENT'
]
const FEE_TYPES: InstantExchangeFeeType[] = ['PERCENTAGE', 'FIXED']

export const InstantExchangeForm: React.FC<FormProps<InstantExchange>> = ({
  value,
  onChange,
  readOnly
}) => {
  const { t } = useTranslation()
  const set = (patch: Partial<InstantExchange>) =>
    onChange({ ...value, ...patch })
  return (
    <>
      <Callout tone="info" className="text-xs leading-relaxed">
        <Trans
          i18nKey="exchanges.instant.rever_only_banner"
          components={{ b: <b /> }}
        />
      </Callout>
      <SettingsCard
        title={t('exchanges.instant.title')}
        description={t('exchanges.instant.description')}
      >
        <SwitchRow
          label={t('exchanges.instant.enabled_label')}
          description={t('exchanges.instant.enabled_description')}
          checked={value.enabled}
          onChange={(enabled) => set({ enabled })}
          readOnly={readOnly}
        />
      </SettingsCard>
      {value.enabled ? (
        <>
          <SettingsCard
            title={t('exchanges.instant.timing_title')}
            description={t('exchanges.instant.timing_description')}
          >
            <Choice
              label={t('exchanges.instant.timing_title')}
              value={value.timing}
              options={TIMINGS.map((timing) => ({
                value: timing,
                label: t(`exchanges.instant.timing.${timing}`)
              }))}
              onChange={(timing) => set({ timing })}
              readOnly={readOnly}
            />
          </SettingsCard>
          <SettingsCard
            title={t('exchanges.instant.fee_title')}
            description={t('exchanges.instant.fee_description')}
          >
            <Choice
              label={t('exchanges.instant.fee_title')}
              value={value.feeType}
              options={FEE_TYPES.map((feeType) => ({
                value: feeType,
                label: t(`exchanges.instant.fee_type.${feeType}`)
              }))}
              onChange={(feeType) => set({ feeType })}
              readOnly={readOnly}
            />
            {value.feeType === 'PERCENTAGE' ? (
              <div className="flex flex-wrap gap-3">
                <NumberField
                  label={t('exchanges.instant.percentage')}
                  value={value.percentageFeeValue}
                  step={0.1}
                  onChange={(percentageFeeValue) => set({ percentageFeeValue })}
                  readOnly={readOnly}
                />
                <NumberField
                  label={t('exchanges.instant.minimum')}
                  description={t('exchanges.instant.zero_is_none')}
                  value={value.feeFloorAmount / 100}
                  onChange={(amount) =>
                    set({ feeFloorAmount: toCents(amount) })
                  }
                  readOnly={readOnly}
                />
                <NumberField
                  label={t('exchanges.instant.maximum')}
                  description={t('exchanges.instant.zero_is_none')}
                  value={value.feeCapAmount / 100}
                  onChange={(amount) => set({ feeCapAmount: toCents(amount) })}
                  readOnly={readOnly}
                />
              </div>
            ) : value.feeType === 'FIXED' ? (
              <NumberField
                label={t('exchanges.instant.amount')}
                value={value.fixedFeeAmount / 100}
                onChange={(amount) => set({ fixedFeeAmount: toCents(amount) })}
                readOnly={readOnly}
              />
            ) : null}
          </SettingsCard>
        </>
      ) : null}
    </>
  )
}
